import { z } from 'zod';

import { timelineItemSchema } from './console.js';

/**
 * Leads (reemplaza al Google Sheet + WF08). Lectura desde la copia de la plataforma (sync de las
 * DBs viejas cada 5 min) y escritura hacia la DB del stack con el adaptador acotado
 * (flag `console.legacy_writes`).
 */

const isoDate = z.string();

export const LEAD_PRIORITIES = ['alta', 'media', 'baja'] as const;
export const leadPrioritySchema = z.enum(LEAD_PRIORITIES);
export type LeadPriority = z.infer<typeof leadPrioritySchema>;

export const RECOMMENDATIONS = ['approve', 'manual_review', 'reject'] as const;
export const recommendationSchema = z.enum(RECOMMENDATIONS);
export type Recommendation = z.infer<typeof recommendationSchema>;

/** Estado "de negocio" derivado (igual que la columna `estado` del Sheet). */
export const LEAD_STAGES = [
  'pending',
  'qualified',
  'rejected',
  'queued',
  'contacted',
  'opened',
  'replied',
  'bounced',
  'unsubscribed',
  'archived',
  'error',
] as const;
export const leadStageSchema = z.enum(LEAD_STAGES);
export type LeadStage = z.infer<typeof leadStageSchema>;

export const LEAD_STAGE_LABELS: Record<LeadStage, string> = {
  pending: 'Pendiente',
  qualified: 'Cualificado',
  rejected: 'Rechazado',
  queued: 'En cola',
  contacted: 'Enviado',
  opened: 'Abierto',
  replied: 'Respondió',
  bounced: 'Rebotó',
  unsubscribed: 'Baja',
  archived: 'Archivado',
  error: 'Error',
};

export const EMAIL_STATUSES = ['none', 'unverified', 'valid', 'risky', 'invalid'] as const;
export const emailStatusSchema = z.enum(EMAIL_STATUSES);
export type EmailStatus = z.infer<typeof emailStatusSchema>;

export const EMAIL_STATUS_LABELS: Record<EmailStatus, string> = {
  none: 'Sin email',
  unverified: 'Sin verificar',
  valid: 'Válido',
  risky: 'Riesgoso',
  invalid: 'Inválido',
};

/** Status del lead en Instantly (`POST /leads/list` → `status`). */
export const INSTANTLY_LEAD_STATUSES = [
  'active',
  'paused',
  'completed',
  'bounced',
  'unsubscribed',
  'skipped',
] as const;
export const instantlyLeadStatusSchema = z.enum(INSTANTLY_LEAD_STATUSES);
export type InstantlyLeadStatus = z.infer<typeof instantlyLeadStatusSchema>;
export const INSTANTLY_LEAD_STATUS_CODES: Record<InstantlyLeadStatus, number> = {
  active: 1,
  paused: 2,
  completed: 3,
  bounced: -1,
  unsubscribed: -2,
  skipped: -3,
};
export const INSTANTLY_LEAD_STATUS_LABELS: Record<InstantlyLeadStatus, string> = {
  active: 'En secuencia',
  paused: 'Pausado',
  completed: 'Secuencia terminada',
  bounced: 'Rebotó',
  unsubscribed: 'Se dio de baja',
  skipped: 'Salteado',
};

/** Interés marcado en Instantly (`lt_interest_status`); `none` = sin marcar. */
export const INSTANTLY_INTERESTS = [
  'interested',
  'meeting_booked',
  'meeting_completed',
  'won',
  'out_of_office',
  'not_interested',
  'wrong_person',
  'lost',
  'no_show',
  'none',
] as const;
export const instantlyInterestSchema = z.enum(INSTANTLY_INTERESTS);
export type InstantlyInterest = z.infer<typeof instantlyInterestSchema>;
export const INSTANTLY_INTEREST_CODES: Record<Exclude<InstantlyInterest, 'none'>, number> = {
  interested: 1,
  meeting_booked: 2,
  meeting_completed: 3,
  won: 4,
  out_of_office: 0,
  not_interested: -1,
  wrong_person: -2,
  lost: -3,
  no_show: -4,
};
export const INSTANTLY_INTEREST_LABELS: Record<InstantlyInterest, string> = {
  interested: 'Interesado',
  meeting_booked: 'Reunión agendada',
  meeting_completed: 'Reunión hecha',
  won: 'Ganado',
  out_of_office: 'Fuera de la oficina',
  not_interested: 'No interesado',
  wrong_person: 'Persona equivocada',
  lost: 'Perdido',
  no_show: 'No se presentó',
  none: 'Sin marcar',
};

export function instantlyStatusFromCode(code: number | null): InstantlyLeadStatus | null {
  if (code === null) return null;
  const hit = Object.entries(INSTANTLY_LEAD_STATUS_CODES).find(([, c]) => c === code);
  return hit ? (hit[0] as InstantlyLeadStatus) : null;
}

/** `null` → `none`; un código propio del workspace (interés personalizado) → `null`. */
export function instantlyInterestFromCode(code: number | null): InstantlyInterest | null {
  if (code === null) return 'none';
  const hit = Object.entries(INSTANTLY_INTEREST_CODES).find(([, c]) => c === code);
  return hit ? (hit[0] as InstantlyInterest) : null;
}

const boolParam = z.enum(['true', 'false']).transform((v) => v === 'true');

export const LEAD_SORTS = ['created_desc', 'created_asc', 'score_desc', 'activity_desc'] as const;

export const leadsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(10).max(200).default(50),
  q: z.string().trim().max(200).optional(),
  stage: leadStageSchema.optional(),
  minScore: z.coerce.number().int().min(0).max(10).optional(),
  maxScore: z.coerce.number().int().min(0).max(10).optional(),
  category: z.string().trim().max(120).optional(),
  source: z.string().trim().max(60).optional(),
  emailStatus: emailStatusSchema.optional(),
  pushed: boolParam.optional(),
  replied: boolParam.optional(),
  /** Por defecto se ocultan los archivados (salvo que se filtre por stage=archived). */
  includeArchived: boolParam.optional(),
  priority: leadPrioritySchema.optional(),
  /** Status del lead en Instantly (según el sync de `POST /leads/list`). */
  instantlyStatus: instantlyLeadStatusSchema.optional(),
  /** Interés marcado en Instantly. */
  interest: instantlyInterestSchema.optional(),
  sort: z.enum(LEAD_SORTS).default('created_desc'),
});
export type LeadsQuery = z.input<typeof leadsQuerySchema>;
/** Filtros ya parseados (lo que ve la API). */
export type LeadsFilter = z.infer<typeof leadsQuerySchema>;

export const leadRowSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  tenantSlug: z.string(),
  company: z.string().nullable(),
  url: z.string().nullable(),
  domain: z.string().nullable(),
  email: z.string().nullable(),
  emailStatus: emailStatusSchema,
  stage: leadStageSchema,
  status: z.string(),
  outreachStatus: z.string().nullable(),
  qualified: z.boolean().nullable(),
  /** Score efectivo (override > qualifier). */
  score: z.number().nullable(),
  recommendation: z.string().nullable(),
  category: z.string().nullable(),
  source: z.string().nullable(),
  priority: leadPrioritySchema.nullable(),
  hasNotes: z.boolean(),
  pushedAt: isoDate.nullable(),
  lastActivityAt: isoDate.nullable(),
  createdAt: isoDate,
  /** Se puede escribir en el stack (fila de la DB vieja + flag prendido). */
  writable: z.boolean(),
});
export type LeadRow = z.infer<typeof leadRowSchema>;

export const leadsResponseSchema = z.object({
  rows: z.array(leadRowSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type LeadsResponse = z.infer<typeof leadsResponseSchema>;

export const leadFacetsSchema = z.object({
  categories: z.array(z.object({ value: z.string(), count: z.number() })),
  sources: z.array(z.object({ value: z.string(), count: z.number() })),
});
export type LeadFacets = z.infer<typeof leadFacetsSchema>;

export const leadHistoryEntrySchema = z.object({
  id: z.uuid(),
  at: isoDate,
  action: z.string(),
  actorEmail: z.string().nullable(),
  diff: z.unknown(),
});
export type LeadHistoryEntry = z.infer<typeof leadHistoryEntrySchema>;

/** Foto del lead en Instantly (una por campaña en la que está). */
export const instantlyLeadStateSchema = z.object({
  campaignName: z.string().nullable(),
  status: instantlyLeadStatusSchema.nullable(),
  statusCode: z.number().nullable(),
  interest: instantlyInterestSchema.nullable(),
  interestCode: z.number().nullable(),
  opens: z.number(),
  replies: z.number(),
  clicks: z.number(),
  /** Paso de la secuencia alcanzado (base 1, como el webhook) y su variante. */
  stepReached: z.number().nullable(),
  variantReached: z.number().nullable(),
  lastContactedAt: isoDate.nullable(),
  lastOpenedAt: isoDate.nullable(),
  lastRepliedAt: isoDate.nullable(),
  lastClickedAt: isoDate.nullable(),
  lastInterestChangeAt: isoDate.nullable(),
  lastTouchAt: isoDate.nullable(),
  syncedAt: isoDate,
});
export type InstantlyLeadState = z.infer<typeof instantlyLeadStateSchema>;

export const leadDetailSchema = leadRowSchema.extend({
  notes: z.string().nullable(),
  archivedAt: isoDate.nullable(),
  qualifiedAt: isoDate.nullable(),
  reasons: z.array(z.string()),
  redFlags: z.array(z.string()),
  sellsOnAmazon: z.boolean().nullable(),
  productCountEstimate: z.string().nullable(),
  emailSource: z.string().nullable(),
  /** Score/recomendación del qualifier, sin override. */
  qualifierScore: z.number().nullable(),
  qualifierRecommendation: z.string().nullable(),
  scoreOverride: z.number().nullable(),
  recommendationOverride: recommendationSchema.nullable(),
  personalization: z.string().nullable(),
  personalizationOverride: z.string().nullable(),
  /** `legacy_db`, `instantly`… Sólo `legacy_db` se puede editar. */
  sourceSystem: z.string(),
  legacyWritesEnabled: z.boolean(),
  /** Estado según Instantly (vacío si el lead nunca estuvo en una campaña sincronizada). */
  instantly: z.array(instantlyLeadStateSchema),
  timeline: z.array(timelineItemSchema),
  history: z.array(leadHistoryEntrySchema),
});
export type LeadDetail = z.infer<typeof leadDetailSchema>;

/**
 * Cambios de un lead con chequeo optimista: `expected` es lo que la persona VIO antes de editar.
 * Si en el stack el valor ya es otro (alguien lo cambió desde el Sheet, n8n u otra pestaña), 409.
 */
const nullableText = (max: number) => z.string().max(max).nullable();

export const leadChangesSchema = z
  .object({
    notes: nullableText(5000),
    priority: leadPrioritySchema.nullable(),
    scoreOverride: z.number().int().min(1).max(10).nullable(),
    recommendationOverride: recommendationSchema.nullable(),
    personalizationOverride: nullableText(2000),
  })
  .partial();
export type LeadChanges = z.infer<typeof leadChangesSchema>;

export const updateLeadSchema = z
  .object({ changes: leadChangesSchema, expected: leadChangesSchema })
  .refine((v) => Object.keys(v.changes).length > 0, { error: 'No hay cambios' })
  .refine((v) => Object.keys(v.changes).every((k) => k in v.expected), {
    error: 'Falta el valor esperado de algún campo (chequeo optimista)',
  });
export type UpdateLead = z.infer<typeof updateLeadSchema>;

export const OVERRIDE_FIELDS = [
  'scoreOverride',
  'recommendationOverride',
  'personalizationOverride',
] as const;

export const leadCommandResponseSchema = z.object({ lead: leadDetailSchema });

/** Estados del stack viejo (`leads_raw.status`) desde los que se puede re-encolar. */
export const REQUEUE_FROM = ['error', 'pending', 'processed'] as const;
